import React, { useCallback, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Box } from '@atlaskit/primitives';
import { typography } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { DraftContext } from 'src/components/bbc-atlaskit-editor/index';
import { ExpandablePanel } from 'src/components/sidebar/src/expandable-panel';
import { useIntl } from 'src/hooks/intl';
import { getContainerId } from 'src/redux/pull-request/selectors';
import { getCurrentFabricUser } from 'src/selectors/conversation-selectors';
import store from 'src/utils/store';

import { getDraftLocalStorageKey } from '../utils';

import { DescriptionSection } from './pull-request-details-description';

const messages = defineMessages({
  descriptionPanelLabel: {
    id: 'frontbucket.repository.pullrequest.descriptionPanelLabel',
    description: 'Label for the description section of a pull request',
    defaultMessage: 'Pull request description',
  },
  descriptionPanelHeader: {
    id: 'frontbucket.repository.pullrequest.descriptionPanelHeader',
    description: 'Header for the pull request description',
    defaultMessage: 'Description',
  },
  draftIndicator: {
    id: 'frontbucket.repository.pullrequest.draftIndicator',
    defaultMessage: 'Unsaved changes',
    description: "Label that indicates there's a draft for the field",
  },
});

export const DescriptionHeader = styled.h2({
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  height: token('space.300', '24px'),
  ...(typography.h400() as any),
});

// @ts-ignore
const DraftIndicatorStyled = styled.div(typography.h100() as any, {
  marginLeft: `${token('space.100', '8px')}`,
  marginTop: 0,
  flexShrink: 0,
});

const DraftIndicator = ({
  testId,
  children,
}: {
  testId?: string;
  children: React.ReactNode;
}) => {
  return (
    <DraftIndicatorStyled data-testid={testId}>{children}</DraftIndicatorStyled>
  );
};

const DESCRIPTION_KEY = 'pull-request.overview.description.expanded';

export const DescriptionPanel = () => {
  const [isEditMode, setEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const intl = useIntl();
  const toggleExpand = useCallback(
    () => setIsExpanded(panelExpanded => !panelExpanded),
    []
  );
  const containerId = useSelector(getContainerId);
  const currentUser = useSelector(getCurrentFabricUser);
  const draftLocalStorageKey = getDraftLocalStorageKey(
    containerId,
    currentUser?.id
  );
  const [hasDraft, setHasDraft] = useState(!!store.get(draftLocalStorageKey));

  const handleDraftChanges = useCallback(() => {
    setHasDraft(!!store.get(draftLocalStorageKey));
  }, [draftLocalStorageKey]);

  const handleEditMode = useCallback((isEditing: boolean) => {
    setEditMode(isEditing);
  }, []);

  const shouldShowDraftIndicator = hasDraft && !isEditMode;

  const Header = (
    <DescriptionHeader>
      <FormattedMessage {...messages.descriptionPanelHeader} />
      {shouldShowDraftIndicator && (
        <DraftIndicator testId="draftIndicator">
          {`• ${intl.formatMessage(messages.draftIndicator)}`}
        </DraftIndicator>
      )}
    </DescriptionHeader>
  );

  return (
    <DraftContext.Provider value={{ handleDraftChanges, handleEditMode }}>
      <Box
        as="section"
        aria-label={intl.formatMessage(messages.descriptionPanelHeader)}
        role="region"
      >
        <ExpandablePanel
          header={Header}
          storageKey={DESCRIPTION_KEY}
          onExpandChange={toggleExpand}
        >
          <DescriptionSection isContainingPanelExpanded={isExpanded} />{' '}
        </ExpandablePanel>
      </Box>
    </DraftContext.Provider>
  );
};
